<template>
  <div class="mc-loads-table">
    <div class="clearfix mb-4">
      <div class="d-flex mc-list-header">
        <h4 class="card-title flex-grow-1">{{ $t('maintenance.order.serviceFeeList') }}</h4>
        <McButton
          v-if="$route.params.id && selectedIdsLength"
          variant="secondary-red"
          class="mx-2 ms-auto"
          icon="icon-outline_trash"
          :text="$t('global.delete')"
          @click="$refs['mc-modal-delete'].show()"
        />

        <McButton
          v-if="$route.params.id && (
            isMGMAndMO ?
            ['Draft', 'Verification', 'Confirmed', 'InProgress'].includes(status)
            :status === 'Draft' || status === 'Verification'
          )"
          variant="primary"
          class="ms-auto"
          :text="$t('global.create')"
          icon="icon-fill_plus"
          :disabled="disabledGlobal"
          @click="handleAdd"
        />
      </div>
    </div>

    <b-card>
      <b-row align-h="end">
        <b-col md="8" xl="10">
          <h6 class="mc-row-check mt-0" v-if="selectedIdsLength">
            <span class="badge btn-success">{{ selectedIdsLength }}</span>
            {{ $t('global.object.selectedRow') }}
            <a @click="handleUnselectAll" class="color-primary">{{
              $t('global.action.deselect')
            }}</a>
          </h6>
          <McAlert
            v-if="invalidKeysLength && !selectedIdsLength"
            variant="error"
            text="Baris data error."
          >
            <template #prefix>
              <div class="mc-alert__custom_prefix">
                <p>{{ invalidKeysLength }}</p>
              </div>
            </template>
          </McAlert>
        </b-col>
        <b-col md="4" xl="2">
          <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
            <template #prepend>
              <i class="ps-2 mc-icon icon-fill_search"></i>
            </template>
            <b-form-input
              :placeholder="`${$t('global.search')}...`"
              :disabled="disabledGlobal"
              :value="search"
              @input="handleSearchChange"
              ref="searchInput"
            ></b-form-input>
            <template #append v-if="search">
              <button
                class="clear-search-btn"
                :disabled="disabledGlobal"
                @click="handleClearSearch"
              >
                <i class="pe-2 mc-icon icon-outline_close"></i>
              </button>
            </template>
          </b-input-group>
        </b-col>
      </b-row>

      <FormulateForm
        v-model="formValues"
        name="editable-table-form"
        ref="formRef"
        :errors="formulateFormErrors"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
      >
        <div class="table-responsive border border-1 mc-loads-table__editable-table">
          <b-table
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            :per-page="limit"
            fixed
            show-empty
            class="editable-table"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2"></b-spinner>
                <strong>{{ $t('global.text.loading') }}...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">{{ $t('global.emptyTable') }}</div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template #head(checkbox)>
              <McCheckbox
                v-model="selectedAll"
                :disabled="disabledGlobal"
                @change="handleSelectAll"
              />
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div class="d-flex justify-content-between" @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div>
                    <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                      <i class="mc-icon icon-outline_chevron-up"></i>
                    </div>
                    <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                      <i class="mc-icon icon-outline_chevron-down"></i>
                    </div>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template #cell(checkbox)="data">
              <McCheckbox
                :checked="selectedIds[data.item.labourId]"
                :disabled="disabledGlobal"
                @change="(event) => handleSelect(event, data)"
              />
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value, item }">
              <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                <FormulateInput
                  v-bind="{ ...field }"
                  :fullObject="fullObjects[field.name]"
                  :rowItem="item"
                  :label="null"
                  :name="field.name"
                  :type="field.type || 'text'"
                  :disabled="isSubmitting"
                  :help="field?.validation?.includes('required')"
                  class="p-0"
                  validation-behavior="live"
                  error-behavior="value"
                  size="sm"
                  @update:fullObject="($event) => $set(fullObjects, field.name, $event)"
                />
              </template>

              <!-- Bold First Column -->
              <template v-else-if="field.isRedirectable">
                <div class="fw-bold text-primary">
                  {{ value }}
                </div>
              </template>

              <!-- Show Only Column -->
              <template v-else-if="!field.isEditable && isEditingRow == index">
                <div>
                  {{ showOnlyValue(value, field) }}
                </div>
              </template>

              <template v-else>
                <div
                  class="td__overflow-ellipsis"
                  :class="
                    invalidItems[item.id] && invalidItems[item.id][field.name]
                      ? 'td__invalid-data'
                      : undefined
                  "
                >
                  {{ transformCellValue(value, field) }}
                </div>
              </template>
            </template>

            <!-- Slot: Action Column (Last Column) -->
            <template #cell(action)="data">
              <div v-if="isEditingRow == data.index">
                <b-button variant="outline" class="__accept-button btn" type="submit">
                  <i class="mc-icon icon-fill_checkmark"></i>
                </b-button>

                <b-button
                  variant="outline"
                  class="__cancel-button btn"
                  @click="handleEditCancel(data)"
                >
                  <i class="mc-icon icon-outline_close"></i>
                </b-button>
              </div>

              <b-button
                v-else
                variant="outline"
                :class="{
                  '__edit-button btn': true,
                  __disabled: isEditingRow !== null || isBusy,
                }"
                @click="handleEditStart(data)"
              >
                <i class="mc-icon icon-outline_edit"></i>
              </b-button>
            </template>
          </b-table>
        </div>
      </FormulateForm>

      <div class="d-flex flex-wrap flex-md-nowrap">
        <div class="mc-limit">
          <div class="__limit">
            <div>{{ $t('global.show') }}</div>
            <b-form-select
              class="form-select form-select-sm __limit--dropdown"
              size="sm"
              :disabled="disabledGlobal"
              :options="limitOptions"
              :value="limit"
              @input="handleLimitChange"
            >
            </b-form-select>
            <div>{{ $t('global.object.data') }}.</div>
          </div>

          <div class="__info" v-if="totalCount === 0">
            <strong>{{ totalCount }}</strong> {{ $t('global.object.from') }}
            <strong>{{ totalCount }}</strong> {{ $t('global.object.data') }}.
          </div>

          <div class="__info" v-else>
            <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
            {{ $t('global.object.from') }} <strong>{{ totalCount }}</strong>
            {{ $t('global.object.data') }}.
          </div>
        </div>
        <div class="mc-pagination-container">
          <b-pagination
            :disabled="disabledGlobal"
            :per-page="limit"
            :total-rows="totalCount"
            :value="page"
            @change="handlePageChange"
          ></b-pagination>
        </div>
      </div>

      <b-modal
        class="mc-modal"
        id="mc-modal-delete"
        ref="mc-modal-delete"
        :title="$t('global.delete')"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
            {{ $t('global.confirmDelete2') }}
            ?
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
          </div>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import { Helper } from '@/mixin/helper'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import McCheckbox from '@/components/McCheckbox'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McAlert,
    McCheckbox,
  },

  mixins: [Helper],
  props: {
    apiClient: { required: true },
    status: { type: String },
    limitOptions: {
      default: () => [5, 10, 25, 50, 100],
    },
    // invalidFields: { type: Array },
    fields: { type: Array },
    listDatas: [],
    addData: {},
  },
  data() {
    return {
      isBusy: true,
      isSubmitting: false,
      limit: this.limitOptions[0] ?? 10,
      page: 1,
      totalCount: 0,
      ids: [],
      items: [],
      sorts: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      isEditingRow: null,
      search: '',
      formValues: {},
      isAdding: null,
      loadsData: {},
      showResponse: {},
      invalidItems: {},
      formulateFormErrors: {},
      fullObjects: {},
      render: true,
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    disabledGlobal() {
      return this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    computedFields() {
      if (
        this.isMGMAndMO
          ? !['Draft', 'Verification', 'Confirmed', 'InProgress'].includes(this.status)
          : this.status !== 'Draft' && this.status !== 'Verification'
      ) return [...this.fields]
      else
        return [
          {
            key: 'checkbox',
            label: '',
            thStyle: 'width: 40px',
          },
          ...this.fields,
          {
            key: 'action',
            label: '',
            thClass: 'mc-action-col-head',
            tdClass: 'mc-action-cell',
          },
        ]
    },
    invalidKeysLength() {
      return Object.keys(this.invalidItems).length
    },
  },
  watch: {
    async formValues(newValue, oldValue) {
      if (this.isEditingRow == null && !this.invalidKeysLength) return

      // get the field that is currently changing
      const [patchData, hasChanged] = this.filterUnchangedData(this.formValues, this.loadsData)

      if (!hasChanged) return

      const changedFields = Object.keys(patchData)
      changedFields.forEach((field) => {
        if (!this.formulateFormErrors[field]) return

        // delete the field in formulateFormErrors
        this.$delete(this.formulateFormErrors, field)
      })
    },
    'formValues.labour': async function (newValue, oldValue) {
      if (newValue === oldValue) {
        return
      }

      if (newValue !== oldValue) {
        if (!newValue) return

        const checkData = await this.apiClient.getById(newValue)
        const valueData = this.listDatas.filter((item) => item.labourId == newValue)

        let data = checkData.labour2ServiceOrder ?? valueData[0]

        this.$set(this.formValues, 'fee', data.fee)
        this.$set(this.formValues, 'qty', data.qty ?? 1)
      }
    },
    'formValues.price': function (newValue, oldValue) {
      if (newValue === oldValue) return
    },
  },

  async mounted() {
    this.fields.forEach((el) => {
      if (el.isSortable) el._sort = 'desc'
      if (el.orderBy) this.sorts.push({ [el.key]: el.orderBy })
    })

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
      sorts: this.stringSorts(),
    })
  },

  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    stringSorts() {
      return this.sorts.reduce((next, ob) => {
        const str = JSON.stringify(ob)
        return [...next, str]
      }, [])
    },
    handleFailedValidation() {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    showOnlyValue(value, field) {
      if (!this.formValues.labour || !value) return `- ${field.afterText ?? ''}`

      return `${value} ${field.afterText ?? ''}`
    },
    transformCellValue(value, field) {
      if (field.name === 'price') return value ? `Rp ${this.thousandFormat(value)}` : 'Rp 0'

      if (value === false) return `- ${field.afterText ?? ''}`
      if (typeof value === 'number') return `${value.toLocaleString()} ${field.afterText ?? ''}`
      return value
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    AssignInvalidErrors() {
      if (!this.invalidFields.length) return
      const indexObject = {}

      // format the invalidFields from array to object with id as the key
      for (const load of this.invalidFields) {
        const loadObj = {}

        for (const field in load) {
          if (field === 'id') continue
          loadObj[field] = ['Data telah dihapus dari sistem.']
        }

        indexObject[load.id] = loadObj
      }

      // assign to invalidItems as state that could be used to show error
      this.invalidItems = indexObject
    },
    async getData(opts) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      const filter = {
        pagination: {
          page: opts.page,
          show: opts.limit,
        },
        search: opts.search,
      }

      this.isBusy = true
      const res = await this.apiClient.get(filter)

      if (!res) return

      this.items = res.data
      if (this.items.length == 0 && this.addData && !opts.search) this.handleAdd()

      this.ids = res.data
      this.page = res.pagination.page
      this.totalCount = res.pagination.total

      this.isBusy = false
    },

    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),

    handleClearSearch() {
      this.handleSearchChange(undefined)
    },

    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      })
    },

    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
    },

    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },

    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return

      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.getById(data.item.labourId)
      if (!refetchedData) return

      const loadData = refetchedData.labour2ServiceOrder
      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item.labourId == data.item.labourId) return loadData
        return item
      })

      // assign formValues with response show data
      this.fields.forEach((field) => {
        if (!field.isEditable) return
        let value

        if (field.name === 'labour') value = loadData.labourId
        else value = loadData[field.name]

        this.fullObjects.labour = loadData.labour
        this.$set(this.formValues, field.name, value)
      })

      this.isEditingRow = data.index
      this.showResponse = { ...loadData }

      this.loadsData = this.formValues

      // assign the form errors from invalidItems with the current id
      if (this.invalidItems[loadData.labourId]) {
        this.formulateFormErrors = { ...this.invalidItems[loadData.labourId] }
      }

      await this.$nextTick()
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formValues = {}
      this.fullObjects = {}

      this.formulateFormErrors = {}

      this.isEditingRow = null
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)
      if (!createdData) return false

      if (!createdData.isSuccessful) {
        this.$mcToast.warning('Data tidak berhasil ditambahkan')

        return false
      }
      this.$mcToast.success('Data berhasil ditambahkan')

      const refetchedData = await this.apiClient.getById(
        createdData.createLabour2ServiceOrder.labourId
      )
      if (!refetchedData.isSuccessful) return false

      this.$set(this.fullObjects, 'labour', refetchedData.labour2ServiceOrder.labour)
      this.ids.push(refetchedData.labour2ServiceOrder.labourId)
      this.totalCount += 1

      return {
        ...data,
        ...refetchedData.labour2ServiceOrder,
      }
    },
    async submitUpdate(data, oldData) {
      const [patchData, hasChanged] = this.filterUnchangedData(data, oldData)

      if (!hasChanged) {
        this.isSubmitting = false
        this.handleEditCancel({ index: this.isEditingRow })
        return
      }

      let deleteData = null
      let createData = null
      let updatedData = null
      let refetchedData = null

      if (oldData.labourId !== data.labour) {
        createData = await this.apiClient.store(data)
        if (!createData.isSuccessful) return false

        refetchedData = await this.apiClient.getById(createData.createLabour2ServiceOrder.labourId)

        deleteData = await this.apiClient.delete(oldData.labourId)
        if (!deleteData.isSuccessful) {
          const deleteNewData = await this.apiClient.delete(
            createData.createLabour2ServiceOrder.labourId
          )

          return false
        }
      } else {
        updatedData = await this.apiClient.update(oldData.labourId, oldData, data)
        if (!updatedData.isSuccessful) return false

        refetchedData = await this.apiClient.getById(data.labour)
      }

      this.loadsData = refetchedData.labour2ServiceOrder

      if (!refetchedData.isSuccessful) {
        this.$mcToast.warning('Perubahan data tidak berhasil disimpan')
        return false
      }

      if (this.invalidItems[oldData.labourId]) {
        this.$delete(this.invalidItems, oldData.labourId)
        this.formulateFormErrors = {}
      }

      this.$mcToast.success('Perubahan data berhasil disimpan')

      return {
        ...refetchedData.labour2ServiceOrder,
      }
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      if (Object.keys(this.formulateFormErrors).length) {
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.fields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }
      // If newData is false, it means there is an error
      if (newData === false) {
        this.isSubmitting = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
      this.formValues = {}
      this.fullObjects = {}
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        if (newData[key] == oldData[key]) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)

      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const promises = []
      for (let key in this.selectedIds) {
        if (this.selectedIds[key]) {
          promises.push(this.apiClient.delete(key))
        }
      }
      await Promise.all(promises)
      if (this.search) this.handleClearSearch()
      await this.getData({
        limit: this.limit,
        page: 1,
      })

      for (const selectedid in this.selectedIds) {
        if (this.invalidItems[selectedid]) {
          this.$delete(this.invalidItems, selectedid)
        }
      }

      await this.$nextTick()
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      this.$mcToast.success('Data berhasil dihapus')
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.labourId]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(
          this.ids.map((data) => [typeof data === 'number' ? data : data.labourId, true])
        )
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.items = [
        {
          labourId: 0,
          labour: {
            name: '',
          },
          qty: 0,
          fee: 0,
        },
        ...this.items,
      ]
    },
    setFullObject(field, value) {
      this.fullObjects[field] = value
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
